// Row-level pieces for the user page's sections. Adapted from the prototype in
// src/pages/UserPagePrototype/parts.js, which stays put as the design record — this
// is the production copy, the same split DriverProfile/components.js already uses,
// so deleting that folder does not break the page.
//
// Everything here is row-level or smaller except TabGrid, which is the one layout
// decision the variant makes and the reason the cards stopped being an accordion
// stack. Section framing itself is DriverProfile's `SectionCard`, imported rather
// than re-cut: the driver record and the user page are now the same idiom.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Stack, Typography, Button, IconButton, Skeleton, Menu, MenuItem } from '../../ui/core';
import { hairline, softFill } from '../../ui/styles';
import { Field } from '../DriverProfile/components';
import { EditIcon, PlusIcon, MoreVertIcon } from '../../ui/icons';
import { useCopyToClipboard, useToast } from '../../hooks';

/**
 * Whether a section's own fetch is still running, failed, or finished.
 *
 * The redux slice does not model this. `getDriverScore` and its neighbours `catch` and
 * dispatch nothing, so a failed request leaves the slice at `null` — which is the same
 * value it holds before the request starts, and the same value it holds when the driver
 * genuinely has no ratings document. Every card read that one `null` as "loading" and sat
 * on its skeletons forever: a 500, an empty record and an in-flight request were three
 * indistinguishable states showing the same grey bars.
 *
 * Local to the card rather than six new redux flags, because it is the caller of the
 * thunk that knows whether its own call came back — and the thunks already return
 * `{ success: false }` on the way out.
 *
 * @param run a stable callback (wrap it in `useCallback`) that fires the fetch
 * @returns [status, retry] where status is 'loading' | 'failed' | 'done'
 */
export const useSectionFetch = (run) => {
  const [status, setStatus] = useState('loading');

  const attempt = useCallback(() => {
    setStatus('loading');
    return Promise.resolve(run()).then(
      // A thunk that resolves with nothing succeeded; only an explicit `success: false`
      // is a failure. Rejections are caught too — `getPromotions` and friends let a
      // throw escape rather than returning a flag.
      (result) => setStatus(result && result.success === false ? 'failed' : 'done'),
      () => setStatus('failed'),
    );
  }, [run]);

  useEffect(() => {
    attempt();
  }, [attempt]);

  return [status, attempt];
};

/** Row-height grey bars, for a section whose fetch is still out. */
export const SectionSkeleton = ({ rows = 2 }) => (
  <Stack spacing={0.5} sx={{ px: 1.75, py: 1.5 }}>
    {Array.from({ length: rows }, (_, i) => (
      <Skeleton key={i} variant="rectangular" height={22} />
    ))}
  </Stack>
);

/**
 * A section whose fetch failed, with its retry.
 *
 * Deliberately not an empty state. "No payments yet" and "we could not ask" are different
 * facts, and a support agent acting on the first when it was really the second is the
 * failure this exists to prevent.
 */
export const SectionError = ({ children, onRetry }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 1.75, py: 1.5 }}>
    <Typography sx={{ fontSize: '12.5px', color: 'error.main', flex: 1, minWidth: 0 }}>{children}</Typography>
    {onRetry && (
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        onClick={onRetry}
        sx={{ textTransform: 'none', fontSize: '12px', flexShrink: 0 }}
      >
        Retry
      </Button>
    )}
  </Stack>
);

/**
 * The narrowest a column may get before the grid gives up and stacks.
 *
 * A measurement, not a taste: the widest row on these tabs is a note (62px date
 * gutter + body + author) and the widest header is Rider money's. 420 clears both
 * with the text still reading as prose. `auto-fit` + `minmax` drops to ONE column
 * rather than holding two at a width neither fits — two columns squeezed to ~460 is
 * what wraps a section title onto two lines.
 */
const MIN_COLUMN = 420;

/**
 * The track sizing, wrapped in `min()` so the floor never exceeds the container.
 *
 * A bare `minmax(420px, 1fr)` does NOT stack on a phone — it drops to one column and
 * then holds that column at 420px, because a grid track never shrinks below its own
 * minimum. On a 375px screen the card is ~270px wide, so the column overhung it by
 * 150px, and every ancestor here sets `overflow: hidden` (surfaceCardSx does), so the
 * excess was CLIPPED rather than scrollable: the end of the email, the ids and each
 * card's right edge were simply unreachable.
 *
 * `min(420px, 100%)` keeps 420 as the two-column threshold on every screen that has
 * the room, and lets the single column shrink to the container below it.
 */
const COLUMN_TRACK = `repeat(auto-fit, minmax(min(${MIN_COLUMN}px, 100%), 1fr))`;

/**
 * A tab as two independently-packing columns, with an optional full-width band on top.
 *
 * Two `Stack`s inside a grid rather than one grid of cards: auto-placed grid items
 * align to the tallest card in their row, so a short card beside a tall one leaves a
 * hole.
 */
export const TabGrid = ({ full, left, right }) => {
  // Both columns can be withheld by permission (the Driver tab does exactly that), and
  // an empty grid still draws the divider above it — a rule under the last card with
  // nothing after it.
  const hasColumns = Boolean(left || right);

  return (
    <Stack spacing={2}>
      {full}
      {full && hasColumns && <Box sx={{ borderTop: (t) => `1px solid ${hairline(t)}`, mx: 0.5 }} />}
      {hasColumns && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: COLUMN_TRACK,
            gap: 2,
            // Without this the columns stretch to equal height and their cards grow to
            // fill it — a card should be as tall as its content.
            alignItems: 'start',
          }}
        >
          {/* `minWidth: 0` on each column, or a grid item's automatic `min-width: auto`
              lets one unbreakable string — an account id, a support email — set the
              track's floor and push the column back past the container. */}
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            {left}
          </Stack>
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            {right}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

/**
 * Pushes a SectionCard's `meta` to the far end of the header.
 *
 * `ml: 'auto'` does NOT work there. SectionCard's header is a MUI `Stack` with
 * `spacing={1}`, whose `& > :not(style) + :not(style)` rule outranks the single class
 * an `sx` prop generates. A fragment, so the spacer and the content are both direct
 * Stack children and the spacer's `flex: 1` actually eats the free space.
 */
export const TrailingMeta = ({ children }) => (
  <>
    <Box sx={{ flex: 1, minWidth: 0 }} />
    {children}
  </>
);

/**
 * The quiet count a section header carries: "12 recent", "3 of 4 accept push".
 *
 * Truncates rather than pushing. Most of these are short, but a few are a phrase ("as of
 * Aug 17, 2026, 04:15 PM") that on a phone is wider than the header has left — and since
 * the meta sits BEFORE the action button in the row, an un-shrinkable one shoved the
 * button off the card.
 */
export const Meta = ({ children }) => (
  <Typography
    sx={{
      fontSize: '11.5px',
      color: 'text.disabled',
      whiteSpace: 'nowrap',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    {children}
  </Typography>
);

/**
 * Add and edit, as a circled icon on the trailing edge.
 *
 * Only these two get an icon. Everything else in a header — "Manage credits",
 * "Reverse" — keeps its words, because an icon for those would be a guess.
 */
export const IconAction = ({ kind = 'add', label, onClick, disabled }) => (
  <IconButton
    size="small"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    sx={{
      flexShrink: 0,
      width: 24,
      height: 24,
      border: (t) => `1px solid ${hairline(t)}`,
      color: 'text.secondary',
      '&:hover': { color: 'text.primary', backgroundColor: (t) => softFill(t) },
    }}
  >
    {kind === 'add' ? <PlusIcon sx={{ fontSize: 14 }} /> : <EditIcon sx={{ fontSize: 13 }} />}
  </IconButton>
);

/** A quiet action for the ones that need their words — "Manage credits", "Reverse". */
export const RowAction = ({ children, onClick, danger, disabled }) => (
  <Button
    size="small"
    variant="text"
    onClick={onClick}
    disabled={disabled}
    sx={{
      textTransform: 'none',
      fontSize: '12px',
      minWidth: 0,
      px: 0.75,
      py: 0.15,
      color: danger ? 'error.main' : 'text.secondary',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Button>
);

/**
 * A row's overflow menu — three dots on the trailing edge.
 *
 * Where a destructive per-row action goes. A red "Reverse" printed on every row of a
 * scrollable payment history is the loudest thing in the section and sits one mis-click
 * from the amount it would undo; behind the dots it takes a deliberate second click.
 *
 * Rendered disabled rather than omitted when there is nothing to do, so the trailing edge
 * does not change width from row to row.
 */
export const RowMenu = ({ items, label = 'Row actions', disabled }) => {
  const [anchor, setAnchor] = React.useState(null);
  const close = () => setAnchor(null);
  const usable = (items || []).filter(Boolean);

  return (
    <>
      <IconButton
        size="small"
        disabled={disabled || usable.length === 0}
        aria-label={label}
        title={label}
        aria-haspopup="true"
        aria-expanded={anchor ? 'true' : undefined}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ width: 24, height: 24, flexShrink: 0, color: 'text.secondary' }}
      >
        <MoreVertIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close} PaperProps={{ sx: { minWidth: 180 } }}>
        {usable.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              close();
              item.onClick();
            }}
            sx={{ fontSize: '13px', ...(item.danger ? { color: 'error.main' } : {}) }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/** An internal link to another admin page, styled to sit inside a Row's label. */
export const PageLink = ({ to, children }) => (
  <Typography
    component={Link}
    to={to}
    sx={{
      fontSize: '13px',
      fontWeight: 500,
      color: 'text.primary',
      textDecoration: 'none',
      '&:hover': { textDecoration: 'underline' },
    }}
  >
    {children}
  </Typography>
);

/**
 * A hairline row: when, what, and a right-aligned aside.
 *
 * `atMeta` is a second line under the date — the time, usually. It lives in the gutter
 * rather than on the row's own meta line because "when" is one fact split across two
 * lines, and putting the clock time next to the route left the meta line carrying two
 * unrelated things.
 */
export const Row = ({ at, atMeta, label, meta, right, onClick, first }) => (
  // A flex Box with `gap`, not a `Stack` with `spacing` — Stack's
  // `& > :not(style) + :not(style)` margin rule outranks an `sx` margin on a child, so
  // the trailing aside could not take `ml: auto` to right-align once it wrapped.
  <Box
    onClick={onClick}
    sx={{
      px: 1.75,
      py: 1,
      borderTop: first ? 'none' : (t) => `1px solid ${hairline(t)}`,
      display: 'flex',
      // Wraps below sm. A 68px date gutter, a label, and a variable-width status chip
      // want ~350px; a section card on a phone has ~270px. Held on one line the label
      // was crushed to ~27px while its text — which wraps at word boundaries, not
      // mid-word — overflowed straight across the chip beside it. Wrapped, the aside
      // drops to its own line and the label gets the width back.
      flexWrap: { xs: 'wrap', sm: 'nowrap' },
      columnGap: 1.5,
      rowGap: 0.5,
      alignItems: 'baseline',
      ...(onClick ? { cursor: 'pointer', '&:hover': { backgroundColor: (t) => softFill(t) } } : {}),
    }}
  >
    {at && (
      // 68, not the 62 the prototype measured: that was for "3 Aug", and the real
      // DATE_SHORT here is "08/12/2026". `nowrap` on both lines so a long locale format
      // pushes the column rather than silently wrapping the date onto two lines.
      <Box sx={{ width: 68, flexShrink: 0 }}>
        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', whiteSpace: 'nowrap' }}>{at}</Typography>
        {atMeta && (
          <Typography sx={{ fontSize: '11px', color: 'text.disabled', whiteSpace: 'nowrap' }}>{atMeta}</Typography>
        )}
      </Box>
    )}
    {/* A flex-basis rather than `flex: 1`: it is the basis that decides whether the aside
        still fits on this line, and 150px is about where a route or a note stops reading
        as one word per line. */}
    <Box sx={{ flex: '1 1 150px', minWidth: 0 }}>
      <Typography component="div" sx={{ fontSize: '13px', fontWeight: 500, overflowWrap: 'anywhere' }}>
        {label}
      </Typography>
      {meta && (
        <Typography component="div" sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
          {meta}
        </Typography>
      )}
    </Box>
    {right && (
      // `ml: auto` keeps it on the trailing edge both when it shares the line and when it
      // has wrapped onto its own; `flexShrink: 0` stops a chip being squeezed to nothing
      // instead of wrapping.
      <Box sx={{ flexShrink: 0, ml: 'auto' }}>{right}</Box>
    )}
  </Box>
);

/**
 * The per-ride scores as `label N/M` pairs.
 *
 * EVERY chip carries its denominator, including the 5s. These are not all on one scale —
 * `overall` is `nps_score`, which runs 0–10, and the four beside it run 0–5 — and showing
 * the scale only on the odd one out means the reader has to know which one that is. Once
 * "10/10" is on the row, a bare "5" beside it reads as 5/10.
 *
 * Coloured proportionally rather than at a fixed 3, for the same reason.
 */
const ScoreChips = ({ scores }) => (
  <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ rowGap: 0.25 }}>
    {scores
      .filter(([, n]) => Number.isFinite(n))
      .map(([label, n, outOf = 5]) => (
        <Typography key={label} sx={{ fontSize: '11.5px', color: 'text.disabled', whiteSpace: 'nowrap' }}>
          {label}{' '}
          <Box component="span" sx={{ fontWeight: 600, color: n / outOf <= 0.6 ? 'error.main' : 'text.secondary' }}>
            {n}/{outOf}
          </Box>
        </Typography>
      ))}
  </Stack>
);

/**
 * One review, in either direction.
 *
 * The written text leads, because it is the only part that ever gets read aloud on a
 * call. Silent ratings are drawn too, greyed — filtering to the written ones would make
 * three complaints look like the whole story when six silent fives sit underneath.
 *
 * The rider direction has no scores to show: `was_positive_exp` is a boolean, so it gets
 * one line of verdict rather than a row of numbers there is no metric behind.
 */
export const ReviewRow = ({ review, at, kind = 'driver', first }) => {
  const scores =
    kind === 'driver'
      ? [
          // `overall` is `nps_score`, on 0–10 — see ScoreChips. The scale is not validated
          // server-side (`rating.dto.ts` only says `@IsNumber()`); it is what the score
          // weights require, since 2.3×10 + 1.6×5×4 + 0.45×100 is exactly 100.
          ['overall', review.overall, 10],
          ['talk', review.conversation],
          ['clean', review.cleanliness],
          ['driving', review.driving],
          ['car', review.car],
        ]
      : [];

  return (
    // Wraps below sm, the same way `Row` does and for the same reason: a 68px date
    // gutter and a nowrap author name ("Deleted account" is ~85px) left the review text
    // itself under 40px on a 320px screen, where it spilled across the author beside it.
    <Box
      sx={{
        px: 1.75,
        py: 1.1,
        borderTop: first ? 'none' : (t) => `1px solid ${hairline(t)}`,
        display: 'flex',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        columnGap: 1.5,
        rowGap: 0.25,
      }}
    >
      {/* 68 to match `Row`'s gutter — a Reviews section sits directly under Ratings and
          above Notes, and three sections with three different date columns read as three
          different tables. */}
      <Typography
        sx={{ fontSize: '11.5px', color: 'text.disabled', width: 68, flexShrink: 0, pt: 0.2, whiteSpace: 'nowrap' }}
      >
        {at}
      </Typography>
      <Stack spacing={0.4} sx={{ flex: '1 1 150px', minWidth: 0 }}>
        {review.text ? (
          <Typography sx={{ fontSize: '13px', lineHeight: 1.45 }}>“{review.text}”</Typography>
        ) : (
          <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', fontStyle: 'italic' }}>
            Rated, no comment
          </Typography>
        )}
        {kind === 'driver' ? (
          <ScoreChips scores={scores} />
        ) : (
          <Typography
            sx={{
              fontSize: '11.5px',
              // `positive` is nullable, and an unanswered question is not a complaint —
              // only an explicit false earns the error colour.
              color: review.positive === false ? 'error.main' : 'text.disabled',
            }}
          >
            {review.positive === false ? 'Negative experience' : review.positive ? 'Positive experience' : 'No verdict'}
            {/* Always said, both ways. The endpoint derives `source` from `rating_type` and
                only ever returns 'driver' or 'co_rider', so labelling one and leaving the
                other bare made "Positive experience" read as an unqualified default rather
                than as the driver's opinion. Anything unrecognised falls to driver, which
                is the endpoint's own fallback. */}
            {review.source === 'co_rider' ? ' · from a co-rider' : ' · from the driver'}
          </Typography>
        )}
      </Stack>
      {/* `ml: auto` holds the author on the trailing edge whether it shares the line or
          has wrapped below the review text. */}
      <Stack spacing={0.3} alignItems="flex-end" sx={{ flexShrink: 0, ml: 'auto' }}>
        {/* The author links to their own user page when the account still exists. A
            deleted rater keeps their review with a null name — dropping the row to avoid
            an empty name would lose the complaint. */}
        {review.author?.userId && review.author?.name ? (
          <Typography
            component={Link}
            to={`/users/${review.author.userId}`}
            sx={{
              fontSize: '11.5px',
              color: 'text.secondary',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {review.author.name}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', whiteSpace: 'nowrap' }}>
            {review.author?.name || 'Deleted account'}
          </Typography>
        )}
        {review.didTip && Number.isFinite(review.tipAmount) && review.tipAmount > 0 && (
          <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: 'success.main', whiteSpace: 'nowrap' }}>
            tipped ${review.tipAmount.toFixed(2)}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

/** Signed money, coloured by direction. Credits read green, debits plain. */
export const Amount = ({ value }) => (
  <Typography
    sx={{
      fontSize: '12.5px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      color: String(value).startsWith('+') ? 'success.main' : 'text.primary',
    }}
  >
    {value}
  </Typography>
);

/** A headline number with its caption — the thing a support agent reads first. */
export const Figure = ({ label, value, note, palette }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '19px',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        mt: 0.25,
        color: palette ? `${palette}.main` : 'text.primary',
      }}
    >
      {value}
    </Typography>
    {note && <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', mt: 0.25 }}>{note}</Typography>}
  </Box>
);

/**
 * A row of `Figure`s that keeps its columns when it wraps.
 *
 * A flex row with `flexWrap` does NOT: the items are as wide as their own content, so
 * a wrapped fifth figure lands under whichever neighbour the flow happened to reach —
 * which is exactly what Rider money's TIPPED did, sitting under Cancellation credit.
 * A grid puts it under the first column, where a reader looking down a column expects
 * the next item to be.
 *
 * 132px is the widest figure label ("CANCELLATION CREDIT" wraps to two lines below it)
 * plus room for a currency value; `auto-fit` then fits as many as the column holds.
 */
export const Figures = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      // `min(132px, 100%)`, same as TabGrid's column track and for the same reason: a
      // grid track never shrinks below its own minimum, so a bare 132px floor would
      // overhang — and therefore be clipped by the card — in any container narrower
      // than that. Wrapping it in `min()` costs nothing wherever 132px fits.
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(132px, 100%), 1fr))',
      columnGap: 2,
      rowGap: 2,
      px: 1.75,
      py: 1.5,
    }}
  >
    {children}
  </Box>
);

/**
 * The delta's colour, shared by `ScoreRow` and `TotalScore`.
 *
 * Green above fleet, red below, grey when they are equal. Signed either way, so the sign
 * and the colour say the same thing twice rather than the colour carrying it alone.
 */
const deltaSx = (delta) => ({
  fontSize: '11.5px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  color: delta === 0 ? 'text.disabled' : delta > 0 ? 'success.main' : 'error.main',
});

/** `+0.18`, `−0.24`, `0.00` — the sign is part of the number, not of the colour. */
const signedDelta = (delta) => `${delta === 0 ? '' : delta > 0 ? '+' : '−'}${Math.abs(delta).toFixed(2)}`;

/**
 * One metric against the fleet average.
 *
 * The delta is the number being read — a bare 4.31 out of 5 is unreadable without
 * knowing the fleet sits at 4.55, which is why the live card printed the average as
 * prose beside every row. Same two numbers, laid out so they can be compared instead
 * of subtracted in your head.
 */
export const ScoreRow = ({ label, value, fleet, first }) => {
  const known = Number.isFinite(value) && Number.isFinite(fleet);
  const delta = known ? value - fleet : 0;
  const below = delta < 0;
  return (
    // Wraps below sm. The three numeric columns are fixed-width and nowrap — "fleet
    // 4.30" plus a 46px delta plus a 62px score plus the gaps come to ~205px — which on
    // a ~270px card left the label about 66px. "Conversation" needs 82, so it simply
    // overprinted the fleet figure next to it. The numbers travel as one group so they
    // wrap together and stay a readable comparison.
    <Box
      sx={{
        px: 1.75,
        py: 1.1,
        borderTop: first ? 'none' : (t) => `1px solid ${hairline(t)}`,
        display: 'flex',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        alignItems: 'center',
        columnGap: 1.5,
        rowGap: 0.25,
      }}
    >
      <Typography sx={{ fontSize: '13px', fontWeight: 500, flex: '1 1 auto', minWidth: 0 }}>{label}</Typography>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0, ml: 'auto' }}>
        {Number.isFinite(fleet) && (
          <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', whiteSpace: 'nowrap' }}>
            fleet {fleet.toFixed(2)}
          </Typography>
        )}
        {known && (
          <Typography sx={{ ...deltaSx(delta), width: 46, textAlign: 'right' }}>{signedDelta(delta)}</Typography>
        )}
        <Stack direction="row" alignItems="baseline" spacing={0.25} sx={{ width: 62, justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: below ? 'error.main' : 'text.primary' }}>
            {Number.isFinite(value) ? value.toFixed(2) : '—'}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>/ 5</Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

/**
 * The same trailing layout as a `ScoreRow`, for a section header.
 *
 * Not a tinted pill. A green pill reading "4.68 overall" was tinted by nothing except the
 * fact that a number existed — and it sat above a Driving row that was below fleet. Laid
 * out as the total of the column beneath it, its delta earns its colour the same way the
 * rows' do.
 *
 * `outOf` because the total does NOT share the scale of the rows it heads. The four
 * metrics are 0–5; `totalScore` is a weighted composite on 0–100 —
 * `driver_ratings_calculator.service.ts:129` sums nps 2.3×10 + four metrics 1.6×5 +
 * cancellation 0.45×100, which is exactly 100. Reading "97.29 / 5" is not a rounding
 * quibble, it is a different claim about the driver.
 */
export const TotalScore = ({ value, fleet, outOf = 5 }) => {
  const known = Number.isFinite(value) && Number.isFinite(fleet);
  const delta = known ? value - fleet : 0;
  const below = delta < 0;
  return (
    <>
      {Number.isFinite(fleet) && (
        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', whiteSpace: 'nowrap' }}>
          fleet {fleet.toFixed(2)}
        </Typography>
      )}
      {known && <Typography sx={deltaSx(delta)}>{signedDelta(delta)}</Typography>}
      <Stack direction="row" alignItems="baseline" spacing={0.25}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: below ? 'error.main' : 'text.primary' }}>
          {Number.isFinite(value) ? value.toFixed(2) : '—'}
        </Typography>
        <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>/ {outOf}</Typography>
      </Stack>
    </>
  );
};

/**
 * A grid of facts. Two columns, because a 108px label column plus a value is what
 * fits twice at 880 and once below it.
 *
 * A fact can be read-only (`value`), a live control (`node`), or editable via a
 * dialog (`onEdit`, a hover-revealed pencil). Which is which is not decoration: the
 * page gates the editable five behind `edit_user` and leaves the ids and derived
 * values alone, and a uniformly flat grid hides that.
 *
 * Every `value` is click-to-copy — see CopyValue. A `node` is not: it owns its own
 * click.
 */
/**
 * A fact's value, click-to-copy.
 *
 * Every value in this grid is something an agent retypes somewhere else — an id into a
 * query, a phone into a search box. The value itself is the target rather than a
 * trailing icon: it is the largest hit area the cell already has, and eight copy glyphs
 * in an eight-cell grid is the noise the hover pencil was avoided for.
 *
 * Not a `Button`: MUI's would bring its own font, padding and uppercase, and this has
 * to look exactly like the text it replaces until it is hovered.
 */
const CopyValue = ({ label, value }) => {
  const c = useCopyToClipboard();
  const t = useToast();

  if (!value) return '—';

  const onClick = () => {
    // `navigator.clipboard` is undefined outside a secure context, and the hook writes
    // through it silently — so without this the toast would claim a copy that never
    // happened.
    if (!navigator?.clipboard) return t.error('Clipboard unavailable in this browser');
    c.copy(String(value));
    t.success(`Copied ${String(label).toLowerCase()}`);
  };

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      title="Click to copy"
      sx={{
        p: 0,
        border: 0,
        background: 'none',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        ':hover': { textDecoration: 'underline' },
      }}
    >
      {value}
    </Box>
  );
};

export const Facts = ({ items, columns = 2 }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: `repeat(${columns}, minmax(0, 1fr))` },
      gap: 1.75,
      px: 1.75,
      py: 1.5,
    }}
  >
    {items.map((f) =>
      f.node ? (
        <Box key={f.label}>
          <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', mb: 0.25 }}>{f.label}</Typography>
          {f.node}
        </Box>
      ) : f.onEdit ? (
        <Stack
          key={f.label}
          direction="row"
          alignItems="flex-end"
          spacing={0.5}
          sx={{ minWidth: 0, '&:hover .fact-edit': { opacity: 1 } }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Field label={f.label} value={<CopyValue label={f.label} value={f.value} />} />
          </Box>
          {/* Revealed on hover rather than always shown: six pencils in a six-cell
              grid is noise, and the point is that these fields CAN be edited, not
              that they are asking to be. */}
          <IconButton
            className="fact-edit"
            size="small"
            onClick={f.onEdit}
            aria-label={`Edit ${String(f.label).toLowerCase()}`}
            sx={{ opacity: 0, transition: 'opacity 120ms', width: 20, height: 20, color: 'text.secondary' }}
          >
            <EditIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Stack>
      ) : (
        <Field key={f.label} label={f.label} value={<CopyValue label={f.label} value={f.value} />} />
      ),
    )}
  </Box>
);
